import React, { useEffect, useMemo, useState } from "react";
import { useStoreState } from "easy-peasy";
import axios from '../../services/axios';
import Loader from "../widgets/Loader";
import { Alert, Button } from "react-bootstrap";
import { CSVLink } from "react-csv";

const WebappStatsAdminTest = () => {
    const { apiData: { ACTIONS } } = useStoreState(state => state.actionSlugs);

    const [isFetching, setIsFetching] = useState(false);
    const [graphsData, setGraphsData] = useState({});
    const [isAvailableData, setIsAvailableData] = useState({});

    const fetchDataForGraph = async () => {
        if (!ACTIONS?.webappStats?.adminTest || isFetching) return;

        setIsFetching(true);
        const entries = Object.entries(ACTIONS.webappStats?.adminTest);

        try {
            const results = await Promise.allSettled(
                entries.map(async ([key, stat]) => {
                    if (!stat.url) return [key, null];
                    const response = await axios.get(stat.url).then(response => {
                        return response?.map(data => {
                            const endingDate = new Date(data.ending_date);
                            const startingDate = data.starting_date ? new Date(data.starting_date) : null;
                            return {
                                ...data,
                                starting_date: startingDate
                                    ? startingDate.toLocaleDateString("fr-FR") + " " + startingDate.toLocaleTimeString("fr-FR")
                                    : null,
                                ending_date: endingDate.toLocaleDateString("fr-FR") + " " + endingDate.toLocaleTimeString("fr-FR")
                            }
                        })
                    })
                    return [key, response];
                })
            );

            const dataObject = Object.fromEntries(
                results.filter((r) => r.status === "fulfilled").map((r) => r.value),
            );
            const graphDataObject = Object.fromEntries(
                Object.entries(dataObject).map(([name, value]) => [
                    name,
                    value?.length ? value : [],
                ])
            );
            const isAvailableObject = Object.fromEntries(
                Object.entries(dataObject).map(([name, value]) => [
                    name,
                    !!value?.length,
                ])
            );

            setGraphsData(graphDataObject);
            setIsAvailableData(isAvailableObject);
        } catch (error) {
            Promise.reject(new Error(error));
        } finally {
            setIsFetching(false);
        }
    };

    useEffect(fetchDataForGraph, [])

    const labelPositioningSentences = useMemo(() => graphsData?.trainingReconversionActivity?.length
        ? Object.keys(graphsData.trainingReconversionActivity?.[0])
            ?.filter(key => key?.includes("before_"))
            ?.map(key => key?.split("before_")?.[1])
        : []
        , [graphsData])

    const headersCsv = useMemo(() => {
        return {
            trainingReconversionActivity: graphsData?.trainingReconversionActivity?.length
                ? [
                    { key: 'id', label: 'Participant⋅e' },
                    { key: 'starting_date', label: 'Date début' },
                    { key: 'ending_date', label: 'Date fin' },
                    { key: 'video_choices', label: 'Nombre de choix XP' },
                    { key: 'activity_video_name', label: 'Titre XP' },
                    { key: 'watch_inside_webapp', label: 'Visionné dans la webapp' },
                    { key: 'playbac_duration', label: 'Durée de visionnage (s)' },
                    { key: 'answer_see_hear', label: 'Rép : voir/entendre' },
                    { key: 'answer_surprised', label: 'Rép : surpris' },
                    { key: 'share_with_trainer', label: 'Consentement pdf' },
                    ...labelPositioningSentences?.map((label, index) => {
                        return {
                            key: "before_" + label,
                            label: "Q" + (index + 1) + "_1"
                        }
                    }),
                    ...labelPositioningSentences?.map((label, index) => {
                        return {
                            key: "after_" + label,
                            label: "Q" + (index + 1) + "_2"
                        }
                    })
                ]
                : [],
            internshipActivity: graphsData?.internshipActivity?.length
                ? [
                    { key: 'id', label: 'Participant⋅e' },
                    { key: 'starting_date', label: 'Date début' },
                    { key: 'ending_date', label: 'Date fin' },
                    { key: 'video_choices', label: 'Nombre de choix XP' },
                    { key: 'activity_video_name', label: 'Titre XP' },
                    { key: 'watch_inside_webapp', label: 'Visionné dans la webapp' },
                    { key: 'playbac_duration', label: 'Durée de visionnage (s)' },
                    { key: 'share_with_trainer', label: 'Consentement pdf' },
                    { key: 'favourite_large_professional_field', label: 'Univers pro attire' },
                    { key: 'unknown_large_professional_field', label: 'Univers pro connais moins' },
                    { key: 'final_large_professional_field', label: 'Univers pro voudrais faire une immersion' }
                ]
                : [],
            freeAnswersActivities: graphsData?.freeAnswersActivities?.length
                ? [
                    { key: 'id', label: 'Participant⋅e' },
                    { key: 'activity_title', label: 'Activité' },
                    { key: 'starting_date', label: 'Date début' },
                    { key: 'ending_date', label: 'Date fin' },
                    { key: 'activity_video_name', label: 'Titre XP' },
                    { key: 'share_with_trainer', label: 'Consentement pdf' },
                    { key: 'answer_before', label: 'Réponses avant XP' },
                    { key: 'answer_after', label: 'Réponses après XP' },
                ]
                : [],
        }
    }, [graphsData])

    return <>
        <h2>Statistiques webapp</h2>
        <div className="d-flex align-items-center">
            <p className="mb-0 me-3">Réponses de l'activité "<b>Découvrir des parcours de formation ou de reconversion</b>" :</p>
            {isFetching
                ? <Loader />
                : isAvailableData?.trainingReconversionActivity
                    ? <CSVLink
                        filename="reponses_activite_formation_reconversion"
                        headers={headersCsv?.trainingReconversionActivity}
                        data={graphsData?.trainingReconversionActivity}>
                        <Button variant="primary" size="sm">
                            <i className="fas fa-download" />&nbsp;Télécharger le fichier csv
                        </Button>
                    </CSVLink>
                    : <Alert variant="danger">Donnée non disponible</Alert>
            }
        </div>
        <p className="mt-3 mb-0">Correspondance des phrases pour le positionnement : Q1_1, ..., QX_1, Q1_2, ..., QX_2 :</p>
        <i>Q1_1 = phrase de positionnement avant expérience. Q1_2 = même phrase de positionnement mais après expérience. </i>
        <br />
        <ul>
            {labelPositioningSentences?.map((label, index) => <li key={label + index}>Q{index + 1} : {label}</li>)}
        </ul>
        <p className="mt-3 mb-0">Correspondance des valeurs de positionnement :</p>
        <ul>
            <li>1 : Pas du tout d’accord</li>
            <li>2 : Plutôt en désaccord</li>
            <li>3 : Ni d’accord, ni en désaccord</li>
            <li>4 : Plutôt d’accord</li>
            <li>5 : Tout à fait d’accord</li>
            <li>vide : Je ne souhaite pas me prononcer</li>
        </ul>
        <div className="d-flex align-items-center mt-5">
            <p className="mb-0 me-3">Réponses de l'activité "<b>Préparer une immersion en entreprise</b>" :</p>
            {isFetching
                ? <Loader />
                : isAvailableData?.internshipActivity
                    ? <CSVLink
                        filename="reponses_activite_preparer_une_immersion_en_entreprise"
                        headers={headersCsv?.internshipActivity}
                        data={graphsData?.internshipActivity}>
                        <Button variant="primary" size="sm">
                            <i className="fas fa-download" />&nbsp;Télécharger le fichier csv
                        </Button>
                    </CSVLink>
                    : <Alert variant="danger">Donnée non disponible</Alert>
            }
        </div>
        <div className="d-flex align-items-center mt-5">
            <p className="mb-0 me-3">Réponses des <b>champs libres</b> des activités <b>avant/après</b> l'étape de visionnage de l'<b>expérience immersive</b> :</p>
            {isFetching
                ? <Loader />
                : isAvailableData?.freeAnswersActivities
                    ? <CSVLink
                        filename="reponses_champs_libres_activites"
                        headers={headersCsv?.freeAnswersActivities}
                        data={graphsData?.freeAnswersActivities}>
                        <Button variant="primary" size="sm">
                            <i className="fas fa-download" />&nbsp;Télécharger le fichier csv
                        </Button>
                    </CSVLink>
                    : <Alert variant="danger">Donnée non disponible</Alert>
            }
        </div>
        <i className="mt-3 mb-0">
            Si l'étape n'est pas disponible dans une actitivité : "-- Etape non présente --" est clairement explicitée<br />
            Si l'activité comporte aucune étape de champ libre avant ou après XP, elle ne figure pas dans ce tableau
        </i>
    </>
}

export default WebappStatsAdminTest;